import { defineStore, storeToRefs } from 'pinia';
import { useCampaignStore } from './campaign';
import { useUiStore } from './ui';
import type {
  ContentfulContentListingPage,
  ContentfulContentListingType,
  ContentfulHeaderContentType,
  ContentfulImageType,
  ContentfulLinkType,
  ContentfulServicesMenuType,
  ContentfulSocialMediaLinkType,
  ContentListingPageChildrenType,
} from '@/types/contentful';
import type { ProductsMenu } from '@/types/products';
import { Campaign } from '~/types/campaign';

type HeaderFooterContent = {
  headerLinks: ContentfulLinkType[];
  logo: ContentfulImageType;
  section1Title: string;
  section1TitleLink: ContentfulLinkType;
  section1Links: ContentfulLinkType[];
  section2Title: string;
  section2TitleLink: ContentfulLinkType;
  section2Links: ContentfulLinkType[];
  section3Title: string;
  section3TitleLink: ContentfulLinkType;
  section3Links: ContentfulLinkType[];
  socialMediaLinks: ContentfulSocialMediaLinkType[];
  address: string;
  phoneNumber: string;
};

export type Service = {
  title: string;
  slug: string;
  children?: ContentListingPageChildrenType[];
  parentSlug?: string;
};

export type ServicesMenu = Service[];

export const useMenuStore = defineStore('menu', () => {
  const productsMenu = ref<ProductsMenu[] | null>(null);
  const servicesMenu = ref<ServicesMenu>([]);
  const servicesMenuLabel = ref<string>('Vårt erbjudande');
  const headerFooterContent = ref<HeaderFooterContent | null>(null);
  const headerContent = ref<ContentfulHeaderContentType>({});
  const serviceMenuCache = ref<ContentfulServicesMenuType | undefined>(undefined);

  const setProductsMenu = async () => {
    const { $productService } = useNuxtApp();
    const { data, error } = await $productService.fetchMenu();
    const menu = error ? {} : data;
    productsMenu.value = menu as ProductsMenu[];
  };

  const setServicesMenu = async () => {
    const { fetchEntry } = useContentful();
    const { fetchCampaigns } = useCampaignStore();
    const { campaigns, assortments } = storeToRefs(useCampaignStore());
    let content = serviceMenuCache.value;
    if (!content) {
      const campaignsPromise = fetchCampaigns();
      const serviceMenuPromise = fetchEntry({
        contentType: 'ourServicesMenu',
        include: 2,
      });
      const [_, entry] = await Promise.all([campaignsPromise, serviceMenuPromise]);
      content = entry.data;
      serviceMenuCache.value = content;
    }

    const formatCampaignPages = (
      parent: ContentfulContentListingPage,
      pages: Campaign[],
    ): ContentListingPageChildrenType[] => {
      return pages.map((page) => {
        return {
          title: page.name,
          slug: page.slug,
          parentSlug: parent.fields.slug,
        };
      });
    };

    const formatContentPages = (
      parent: ContentfulContentListingPage,
      pages: ContentfulContentListingType[],
    ): ContentListingPageChildrenType[] => {
      return pages
        .filter((page) => page.fields)
        .map((page) => {
          return {
            title: page.fields.title,
            slug: page.fields.slug,
            parentSlug: parent.fields.slug,
          };
        });
    };

    const getFormattedContentPage = (contentListingPage: ContentfulContentListingPage, slug: string) => {
      switch (slug) {
        case 'kampanjer':
          return formatCampaignPages(contentListingPage, campaigns.value);
        case 'sortiment':
          return formatCampaignPages(contentListingPage, assortments.value);
        default:
          return formatContentPages(contentListingPage, contentListingPage.fields?.content);
      }
    };

    const contentListingPages: Service[] | undefined = content?.fields.services
      .filter(
        (contentListingPage: ContentfulContentListingPage) =>
          contentListingPage?.fields?.content?.length ||
          contentListingPage?.fields?.slug === 'kampanjer' ||
          contentListingPage?.fields?.slug === 'sortiment',
      )
      .map((contentListingPage: ContentfulContentListingPage) => {
        return {
          title: contentListingPage.fields.title,
          slug: contentListingPage.fields.slug,
          icon: contentListingPage.fields.icon?.fields.file.url,
          children: getFormattedContentPage(contentListingPage, contentListingPage?.fields?.slug),
        };
      })
      .filter(
        (contentListingPage: { children: ContentListingPageChildrenType[] }) =>
          contentListingPage.children.length !== 0,
      );

    servicesMenuLabel.value = content!.fields.label;
    servicesMenu.value = contentListingPages!;
  };

  const setHeaderFooterContent = async () => {
    const { fetchEntry } = useContentful();
    const { setMarketingBanners } = useUiStore();
    const entry = await fetchEntry({ contentType: 'footer', include: 2 });
    headerFooterContent.value = entry.data?.fields;

    if (entry.data?.fields?.marketingBannersSection) {
      setMarketingBanners(entry.data?.fields.marketingBannersSection);
    }
  };

  const setHeaderContent = async () => {
    const { fetchEntry } = useContentful();
    const entry = await fetchEntry({ contentType: 'header' });
    headerContent.value = entry.data?.fields;
  };

  return {
    headerContent,
    headerFooterContent,
    productsMenu,
    servicesMenu,
    servicesMenuLabel,
    setProductsMenu,
    setHeaderFooterContent,
    setServicesMenu,
    setHeaderContent,
  };
});
